import React, { useState, useEffect } from 'react';
import { configAPI, tradeSettingsAPI } from '../services/api';
import api from '../services/api';
import { EntryExitConfig, TradeSettings } from '../types';
import { Plus, Edit, Trash2, Power, Settings, Calendar, Percent, Activity, Copy, Filter, CheckSquare } from 'lucide-react';
import { Container, Row, Col, Card, Table, Spinner, Badge, Button } from 'react-bootstrap';
import ConfigForm from '../components/ConfigForm';
import ConfirmationModal from '../components/ConfirmationModal';
import MainLayout from '../components/MainLayout';
import { useTheme } from '../contexts/ThemeContext';

const EntryExitPage: React.FC = () => {
  const { themeConfig } = useTheme();
  const [configs, setConfigs] = useState<EntryExitConfig[]>([]);
  const [selectedType, setSelectedType] = useState<'BUY' | 'SELL'>('BUY');
  const [showForm, setShowForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState<EntryExitConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [cardsVisible, setCardsVisible] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, config: null as EntryExitConfig | null });
  const [toggleModal, setToggleModal] = useState({ isOpen: false, config: null as EntryExitConfig | null });
  const [duplicateModal, setDuplicateModal] = useState({ isOpen: false, config: null as EntryExitConfig | null });
  const [filterModal, setFilterModal] = useState({ isOpen: false, config: null as EntryExitConfig | null });

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkEnableModal, setBulkEnableModal] = useState(false);
  const [bulkFilterModal, setBulkFilterModal] = useState<{ isOpen: boolean; enable: boolean }>({ isOpen: false, enable: true });

  // Form state for header section
  const [niftyExpiryDate, setNiftyExpiryDate] = useState('');
  const [capitalPercentage, setCapitalPercentage] = useState(10);
  const [xPercentage, setXPercentage] = useState(5);
  const [yPercentage, setYPercentage] = useState(3);
  const [xCount, setXCount] = useState('0');
  const [yCount, setYCount] = useState('0');
  const [tradeSettings, setTradeSettings] = useState<TradeSettings | null>(null);

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      await Promise.all([fetchConfigs(), fetchTradeSettings()]);
      setLoading(false);
      fetchLiveCounts(); // non-blocking, don't delay page render
    };

    loadInitialData();
    setNiftyExpiryDate(getNextTuesdayDate());
    setTimeout(() => setCardsVisible(true), 100);

    const configInterval = setInterval(() => { fetchConfigs(); }, 5000);
    const liveCountsInterval = setInterval(() => { fetchLiveCounts(); }, 250);

    return () => {
      clearInterval(configInterval);
      clearInterval(liveCountsInterval);
    };
  }, []);

  // Calculate next Tuesday date
  const getNextTuesdayDate = (): string => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0=Sunday, 1=Monday, 2=Tuesday, ..., 6=Saturday

    let targetDate: Date;
    if (dayOfWeek === 2) {
      // Today is Tuesday
      targetDate = today;
    } else if (dayOfWeek === 1) {
      // Today is Monday, return tomorrow (Tuesday)
      targetDate = new Date(today);
      targetDate.setDate(today.getDate() + 1);
    } else {
      // Wednesday to Sunday, calculate next Tuesday
      const daysUntilTuesday = dayOfWeek === 0 ? 2 : (9 - dayOfWeek);
      targetDate = new Date(today);
      targetDate.setDate(today.getDate() + daysUntilTuesday);
    }

    // Format as YYYY-MM-DD
    const year = targetDate.getFullYear();
    const month = String(targetDate.getMonth() + 1).padStart(2, '0');
    const day = String(targetDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fetchLiveCounts = async () => {
    try {
      const response = await api.get('/live-data/counts', { timeout: 3000 });
      const data = response.data;
      setXCount(data.x_count?.toString() || '0');
      setYCount(data.y_count?.toString() || '0');
    } catch (error) {
      console.error('Failed to fetch live counts:', error);
    }
  };

  const fetchTradeSettings = async () => {
    try {
      const settings = await tradeSettingsAPI.getSettings();
      setTradeSettings(settings);
      setCapitalPercentage(settings.capital_percentage || 10);
      setXPercentage(settings.x_percentage || 5);
      setYPercentage(settings.y_percentage || 3);
    } catch (error) {
      console.error('Failed to fetch trade settings:', error);
    }
  };

  const updateTradeSettings = async () => {
    try {
      if (!tradeSettings) return;

      const updatedSettings = {
        ...tradeSettings,
        capital_percentage: capitalPercentage,
        x_percentage: xPercentage,
        y_percentage: yPercentage
      };
      await tradeSettingsAPI.updateSettings(updatedSettings);
      setTradeSettings(updatedSettings);
    } catch (error) {
      console.error('Failed to update trade settings:', error);
    }
  };

  const fetchConfigs = async () => {
    try {
      const data = await configAPI.getConfigs();
      setConfigs(data);
    } catch (error) {
      console.error('Failed to fetch configs:', error);
    }
  };

  const handleToggleActive = async (config: EntryExitConfig) => {
    setToggleModal({ isOpen: true, config });
  };

  const handleToggleFilter = async (config: EntryExitConfig) => {
    setFilterModal({ isOpen: true, config });
  };

  const confirmToggle = async () => {
    if (toggleModal.config) {
      try {
        await configAPI.updateConfig(toggleModal.config.id!, { is_active: !toggleModal.config.is_active });
        fetchConfigs();
        setToggleModal({ isOpen: false, config: null });
      } catch (error) { console.error('Failed to toggle config:', error); }
    }
  };

  const confirmFilterToggle = async () => {
    if (filterModal.config) {
      try {
        await configAPI.toggleFilter(filterModal.config.id!, !filterModal.config.filter_enabled);
        fetchConfigs();
        setFilterModal({ isOpen: false, config: null });
      } catch (error) { console.error('Failed to toggle filter:', error); }
    }
  };

  const handleDelete = async (config: EntryExitConfig) => {
    setDeleteModal({ isOpen: true, config });
  };

  const handleDuplicate = async (config: EntryExitConfig) => {
    setDuplicateModal({ isOpen: true, config });
  };

  const confirmDuplicate = async () => {
    if (duplicateModal.config) {
      try {
        await configAPI.duplicateConfig(duplicateModal.config.id!);
        fetchConfigs();
        setDuplicateModal({ isOpen: false, config: null });
      } catch (error) { console.error('Failed to duplicate config:', error); }
    }
  };

  const confirmDelete = async () => {
    if (deleteModal.config) {
      try {
        await configAPI.deleteConfig(deleteModal.config.id!);
        fetchConfigs();
        setDeleteModal({ isOpen: false, config: null });
      } catch (error) { console.error('Failed to delete config:', error); }
    }
  };

  const handleEdit = (config: EntryExitConfig) => {
    setEditingConfig(config);
    setNiftyExpiryDate(config.nifty_expiry_date);
    setCapitalPercentage(config.capital_percentage);
    setSelectedType(config.type);
    setShowForm(true);
  };

  const handleAddNew = () => {
    setEditingConfig(null);
    setShowForm(true);
    console.log('Add config clicked, showForm:', true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingConfig(null);
    fetchConfigs();
  };

  const filteredConfigs = configs
    .filter(config => config.type === selectedType)
    .sort((a, b) => selectedType === 'BUY' ? a.x_value - b.x_value : a.y_value - b.y_value);

  const inactiveFilteredConfigs = filteredConfigs.filter(c => !c.is_active);

  const toggleSelectId = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredConfigs.length && filteredConfigs.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredConfigs.map(c => c.id!)));
    }
  };

  const confirmBulkEnable = async () => {
    try {
      const ids = Array.from(selectedIds).filter(id => {
        const c = filteredConfigs.find(x => x.id === id);
        return c && !c.is_active;
      });
      if (ids.length > 0) await configAPI.bulkEnable(ids);
      setSelectedIds(new Set());
      setBulkEnableModal(false);
      fetchConfigs();
    } catch (error) { console.error('Bulk enable failed:', error); }
  };

  const confirmBulkFilter = async () => {
    try {
      await Promise.all(
        Array.from(selectedIds).map(id => configAPI.toggleFilter(id, bulkFilterModal.enable))
      );
      setSelectedIds(new Set());
      setBulkFilterModal({ isOpen: false, enable: true });
      fetchConfigs();
    } catch (error) { console.error('Bulk filter failed:', error); }
  };

  const formatAmount = (amount: number): string => {
    if (amount >= 100000) {
      return `${(amount / 100000).toFixed(1)}L`;
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(1)}K`;
    }
    return amount.toString();
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="text-center">
          <Spinner animation="border" variant="light" style={{ width: '4rem', height: '4rem' }} />
          <div className="mt-3 text-white fw-bold">Loading Configurations...</div>
        </div>
      </div>
    );
  }

  return (
    <MainLayout>
      <style>{`
        .btn-standard {
          padding: 6px 12px;
          font-size: 13px;
          border-radius: 4px;
          border: 1px solid;
          font-weight: 500;
        }

        .type-toggle {
          background: #ffffff;
          border: 1px solid #dee2e6;
          border-radius: 4px;
          padding: 2px;
        }

        .type-btn {
          border-radius: 3px;
          padding: 6px 12px;
          border: none;
          font-weight: 500;
          font-size: 13px;
          transition: all 0.2s ease;
        }

        .type-btn.active {
          background: #007bff;
          color: white;
        }

        .type-btn:not(.active) {
          background: transparent;
          color: #6c757d;
        }

        .action-btn {
          padding: 8px 10px;
          border-radius: 6px;
          border: none;
          transition: all 0.2s ease;
          min-width: 36px;
          min-height: 36px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }

        .action-btn.edit { background: rgba(0, 123, 255, 0.1); color: #007bff; }
        .action-btn.duplicate { background: rgba(108, 117, 125, 0.1); color: #6c757d; }
        .action-btn.filter { background: rgba(255, 193, 7, 0.1); color: #856404; }
        .action-btn.toggle { background: rgba(40, 167, 69, 0.1); color: #28a745; }
        .action-btn.delete { background: rgba(220, 53, 69, 0.1); color: #dc3545; }

        .table-responsive .table tbody tr:nth-child(odd) td {
          background-color: #f5f5f5 !important;
        }
        .table-responsive .table tbody tr:nth-child(even) td {
          background-color: #ffffff !important;
        }
      `}</style>

        {/* Header */}
        {/* <div style={{ background: '#ffffff', border: '1px solid #dee2e6', borderRadius: '4px', margin: '16px', padding: '12px' }}>
          <Container>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <h2 style={{ margin: '0 0 4px 0', fontWeight: '600', fontSize: '18px', color: '#212529' }}>
                  Entry / Exit Configuration
                </h2>
                <p style={{ margin: '0', fontSize: '12px', color: '#6c757d' }}>Manage your trading strategies</p>
              </div>
              <div className="d-flex gap-3">
                <div style={{
                  background: themeConfig.colors.card,
                  border: `4px solid ${themeConfig.colors.border}`,
                  borderRadius: '8px',
                  padding: '12px',
                  boxShadow: `0 2px 4px ${themeConfig.colors.shadow}`
                }}>
                  <label className="form-label" style={{ fontSize: '12px', fontWeight: '600', marginBottom: '6px', display: 'block', color: themeConfig.colors.text }}>
                    X% / X
                  </label>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <input
                      type="number"
                      className="form-control"
                      style={{ borderRadius: '4px', padding: '6px 10px', fontSize: '13px', background: themeConfig.colors.surface, width: '70px', border: `1px solid ${themeConfig.colors.border}`, color: themeConfig.colors.text }}
                      value={xPercentage}
                      readOnly
                      step="0.1"
                    />
                    <input
                      type="text"
                      className="form-control"
                      style={{
                        borderRadius: '4px',
                        padding: '6px 10px',
                        fontSize: '13px',
                        background: themeConfig.colors.primary + '20',
                        fontWeight: '600',
                        width: '50px',
                        border: `1px solid ${themeConfig.colors.border}`,
                        color: themeConfig.colors.text
                      }}
                      value={xCount}
                      readOnly
                    />
                  </div>
                </div>
                <div style={{
                  background: themeConfig.colors.card,
                  border: `4px solid ${themeConfig.colors.border}`,
                  borderRadius: '8px',
                  padding: '12px',
                  boxShadow: `0 2px 4px ${themeConfig.colors.shadow}`
                }}>
                  <label className="form-label" style={{ fontSize: '12px', fontWeight: '600', marginBottom: '6px', display: 'block', color: themeConfig.colors.text }}>
                    Y% / Y
                  </label>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <input
                      type="number"
                      className="form-control"
                      style={{ borderRadius: '4px', padding: '6px 10px', fontSize: '13px', background: themeConfig.colors.surface, width: '70px', border: `1px solid ${themeConfig.colors.border}`, color: themeConfig.colors.text }}
                      value={yPercentage}
                      readOnly
                      step="0.1"
                    />
                    <input
                      type="text"
                      className="form-control"
                      style={{
                        borderRadius: '4px',
                        padding: '6px 10px',
                        fontSize: '13px',
                        background: themeConfig.colors.secondary + '20',
                        fontWeight: '600',
                        width: '50px',
                        border: `1px solid ${themeConfig.colors.border}`,
                        color: themeConfig.colors.text
                      }}
                      value={yCount}
                      readOnly
                    />
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </div> */}

        <Container className="pb-4">
          {/* Header Configuration */}
          <Card style={{ background: '#f0e6ff', border: '1px solid #d4b3ff', borderRadius: '4px', marginBottom: '16px' }}>
            {/* <Card.Header className="bg-transparent border-0 p-3">
              <div className="d-flex align-items-center">
                <div style={{ background: '#007bff', borderRadius: '4px', padding: '4px', color: 'white', marginRight: '8px' }}>
                  <Settings size={16} />
                </div>
                <h5 style={{ margin: '0', fontWeight: '600', fontSize: '14px' }}>Trading Parameters</h5>
              </div>
            </Card.Header> */}
            <Card.Body className="p-3">
              <Row className="g-2 align-items-end">
                <Col md={3}>
                  <label className="form-label" style={{ fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                    <Calendar size={14} className="me-1" />
                    NIFTY Expiry Date
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    style={{ borderRadius: '4px', padding: '6px 10px', fontSize: '13px' }}
                    value={niftyExpiryDate}
                    onChange={(e) => setNiftyExpiryDate(e.target.value)}
                  />
                </Col>

                <Col md={1}>
                  <label className="form-label" style={{ fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                    <Percent size={14} className="me-1" />
                    Capital
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    style={{ borderRadius: '4px', padding: '6px 10px', fontSize: '13px' }}
                    value={capitalPercentage}
                    onChange={(e) => setCapitalPercentage(Number(e.target.value))}
                    onBlur={updateTradeSettings}
                    min="1"
                    max="100"
                  />
                </Col>

                <Col md={3}>
                  <label className="form-label" style={{ fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                    Type
                  </label>
                  <div className="type-toggle d-flex">
                    <button
                      onClick={() => { setSelectedType('BUY'); setSelectedIds(new Set()); }}
                      className={`type-btn flex-fill ${selectedType === 'BUY' ? 'active' : ''}`}
                    >
                      BUY
                    </button>
                    <button
                      onClick={() => { setSelectedType('SELL'); setSelectedIds(new Set()); }}
                      className={`type-btn flex-fill ${selectedType === 'SELL' ? 'active' : ''}`}
                    >
                      SELL
                    </button>
                  </div>
                </Col>

                <Col md={5} className="d-flex justify-content-end">
                  <div>
                    <label className="form-label" style={{ fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                      &nbsp;
                    </label>
                    <button
                      onClick={handleAddNew}
                      style={{
                        background: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '10px 30px',
                        fontSize: '13px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <Plus size={14} />
                      Add Config
                    </button>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Configuration List */}
          <Card style={{ background: '#ffffff', border: '1px solid #dee2e6', borderRadius: '4px' }}>
            <Card.Header className="bg-transparent border-0 p-3">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <div style={{ background: selectedType === 'BUY' ? '#28a745' : '#dc3545', borderRadius: '4px', padding: '4px', color: 'white' }}>
                    <Activity size={16} />
                  </div>
                  <h5 style={{ margin: '0', fontWeight: '600', fontSize: '14px' }}>NSE — {selectedType} CONFIGURATIONS</h5>
                  {selectedIds.size > 0 && (
                    <>
                      {Array.from(selectedIds).some(id => !filteredConfigs.find(c => c.id === id)?.is_active) && (
                        <button
                          onClick={() => setBulkEnableModal(true)}
                          style={{ background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 12px', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}
                        >
                          <CheckSquare size={14} />
                          Enable ({Array.from(selectedIds).filter(id => !filteredConfigs.find(c => c.id === id)?.is_active).length})
                        </button>
                      )}
                      <button
                        onClick={() => setBulkFilterModal({ isOpen: true, enable: true })}
                        style={{ background: '#856404', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 12px', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}
                      >
                        <Filter size={14} />
                        Filter ON ({selectedIds.size})
                      </button>
                      <button
                        onClick={() => setBulkFilterModal({ isOpen: true, enable: false })}
                        style={{ background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 12px', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}
                      >
                        <Filter size={14} />
                        Filter OFF ({selectedIds.size})
                      </button>
                    </>
                  )}
                </div>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {filteredConfigs.length > 0 ? (
                <Table responsive className="mb-0" style={{ fontSize: '12px' }}>
                  <thead style={{ background: '#f8f9fa' }}>
                    <tr>
                      <th style={{ fontWeight: '600', padding: '8px', fontSize: '11px', width: '36px' }}>
                        <input
                          type="checkbox"
                          style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                          checked={filteredConfigs.length > 0 && selectedIds.size === filteredConfigs.length}
                          onChange={toggleSelectAll}
                          title="Select all"
                        />
                      </th>
                      <th style={{ fontWeight: '600', padding: '8px', fontSize: '11px' }}>Name</th>
                      <th style={{ fontWeight: '600', padding: '8px', fontSize: '11px' }}>X</th>
                      <th style={{ fontWeight: '600', padding: '8px', fontSize: '11px' }}>Y</th>
                      <th style={{ fontWeight: '600', padding: '8px', fontSize: '11px' }}>Range</th>
                      <th style={{ fontWeight: '600', padding: '8px', fontSize: '11px' }}>Per Strike Rs</th>
                      <th style={{ fontWeight: '600', padding: '8px', fontSize: '11px' }}>Entry</th>
                      <th style={{ fontWeight: '600', padding: '8px', fontSize: '11px' }}>Exit</th>
                      <th style={{ fontWeight: '600', padding: '8px', fontSize: '11px' }}>Filter</th>
                      <th style={{ fontWeight: '600', padding: '8px', fontSize: '11px' }}>Status</th>
                      <th style={{ fontWeight: '600', padding: '8px', fontSize: '11px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredConfigs.map((config, index) => (
                      <tr key={config.id} style={{ opacity: config.is_active ? 1 : 0.75 }}>
                        <td style={{ padding: '8px' }}>
                          <input
                            type="checkbox"
                            style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                            checked={selectedIds.has(config.id!)}
                            onChange={() => toggleSelectId(config.id!)}
                          />
                        </td>
                        <td style={{ fontWeight: '600', padding: '8px' }}>{config.name}</td>
                        <td style={{ padding: '8px' }}>{config.x_value}</td>
                        <td style={{ padding: '8px' }}>{config.y_value}</td>
                        <td style={{ padding: '8px' }}>₹{Math.round(config.range_min)} - ₹{Math.round(config.range_max)}</td>
                        <td style={{ padding: '8px' }}>₹{formatAmount(config.per_strike_amount)}</td>
                        <td style={{ padding: '8px' }}>
                          <Badge
                            bg={!config.is_active ? 'secondary' : config.entry_active ? 'warning' : 'success'}
                            style={{ padding: '4px 8px', fontSize: '10px', fontWeight: '600', borderRadius: '4px' }}
                          >
                            {!config.is_active ? 'Inactive' : config.entry_active ? 'Watching' : 'Complete'}
                          </Badge>
                        </td>
                        <td style={{ padding: '8px' }}>
                          <Badge
                            bg={!config.is_active ? 'secondary' :
                                !config.entry_active && config.exit_active ? 'warning' :
                                !config.entry_active && !config.exit_active ? 'success' : 'secondary'}
                            style={{ padding: '4px 8px', fontSize: '10px', fontWeight: '600', borderRadius: '4px' }}
                          >
                            {!config.is_active ? 'Inactive' :
                             !config.entry_active && config.exit_active ? 'Watching' :
                             !config.entry_active && !config.exit_active ? 'Complete' : 'Waiting'}
                          </Badge>
                        </td>
                        <td style={{ padding: '8px' }}>
                          <Badge
                            bg={config.filter_enabled ? 'success' : 'secondary'}
                            style={{ padding: '4px 8px', fontSize: '10px', fontWeight: '600', borderRadius: '4px' }}
                          >
                            {config.filter_enabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                        </td>
                        <td style={{ padding: '8px' }}>
                          <Badge
                            bg={config.is_active ? 'success' : 'secondary'}
                            style={{ padding: '4px 8px', fontSize: '10px', fontWeight: '600', borderRadius: '4px' }}
                          >
                            {config.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td style={{ padding: '8px' }}>
                          <div className="d-flex gap-2">
                            <button onClick={() => handleEdit(config)} className="action-btn edit" title="Edit"><Edit size={16} /></button>
                            <button onClick={() => handleDuplicate(config)} className="action-btn duplicate" title="Duplicate"><Copy size={16} /></button>
                            <button onClick={() => handleToggleFilter(config)} className="action-btn filter" title={config.filter_enabled ? 'Disable Filter' : 'Enable Filter'}><Filter size={16} /></button>
                            <button onClick={() => handleToggleActive(config)} className="action-btn toggle" title={config.is_active ? 'Deactivate' : 'Activate'} style={{ background: config.is_active ? 'rgba(40, 167, 69, 0.1)' : 'rgba(220, 53, 69, 0.1)', color: config.is_active ? '#28a745' : '#dc3545' }}><Power size={16} /></button>
                            <button onClick={() => handleDelete(config)} className="action-btn delete" title="Delete"><Trash2 size={16} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center py-4">
                  <div style={{ color: '#6c757d' }}>
                    <div style={{ background: '#6c757d', borderRadius: '4px', padding: '12px', color: 'white', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' }}>
                      <Settings size={24} />
                    </div>
                    <h6 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>No {selectedType.toLowerCase()} configurations found</h6>
                    <small style={{ fontSize: '11px', marginBottom: '8px', display: 'block' }}>Create your first trading configuration</small>
                    <button
                      onClick={handleAddNew}
                      style={{
                        background: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '6px 12px',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      Create Configuration
                    </button>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Container>

      {/* Configuration Form Modal */}
      <ConfigForm
        isOpen={showForm}
        exchange="NSE"
        onSubmit={async (configData) => {
          try {
            if (editingConfig) {
              await configAPI.updateConfig(editingConfig.id!, configData);
            } else {
              await configAPI.createConfig(configData);
            }
            setShowForm(false);
            setEditingConfig(null);
            await fetchConfigs();
          } catch (error: any) {
            console.error('Failed to save config:', error);
            const errorMsg = error.response?.data?.errors
              ? error.response.data.errors.map((e: any) => `${e.param}: ${e.msg}`).join('\n')
              : error.response?.data?.message || 'Failed to save configuration';
            alert(`Error: ${errorMsg}`);
          }
        }}
        config={editingConfig}
        tradeSettings={tradeSettings}
        selectedType={selectedType}
        onClose={handleFormClose}
      />

      {/* Bulk Enable Confirmation Modal */}
      <ConfirmationModal
        isOpen={bulkEnableModal}
        onClose={() => setBulkEnableModal(false)}
        onConfirm={confirmBulkEnable}
        title="Enable Configurations"
        message={`Enable ${Array.from(selectedIds).filter(id => !filteredConfigs.find(c => c.id === id)?.is_active).length} inactive selected configuration(s)?`}
        confirmText="Enable All"
        cancelText="Cancel"
        type="warning"
      />

      {/* Bulk Filter Confirmation Modal */}
      <ConfirmationModal
        isOpen={bulkFilterModal.isOpen}
        onClose={() => setBulkFilterModal({ isOpen: false, enable: true })}
        onConfirm={confirmBulkFilter}
        title={bulkFilterModal.enable ? 'Enable Filter' : 'Disable Filter'}
        message={`${bulkFilterModal.enable ? 'Enable' : 'Disable'} filter for ${selectedIds.size} selected configuration(s)?`}
        confirmText={bulkFilterModal.enable ? 'Enable Filter' : 'Disable Filter'}
        cancelText="Cancel"
        type="warning"
      />

      {/* Duplicate Confirmation Modal */}
      <ConfirmationModal
        isOpen={duplicateModal.isOpen}
        onClose={() => setDuplicateModal({ isOpen: false, config: null })}
        onConfirm={confirmDuplicate}
        title="Duplicate Configuration"
        message={`Create a copy of "${duplicateModal.config?.name}"? The duplicate will be created as inactive.`}
        confirmText="Duplicate"
        cancelText="Cancel"
        type="warning"
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, config: null })}
        onConfirm={confirmDelete}
        title="Delete Configuration"
        message={`Are you sure you want to delete "${deleteModal.config?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Toggle Active Confirmation Modal */}
      <ConfirmationModal
        isOpen={toggleModal.isOpen}
        onClose={() => setToggleModal({ isOpen: false, config: null })}
        onConfirm={confirmToggle}
        title={toggleModal.config?.is_active ? "Disable Configuration" : "Enable Configuration"}
        message={`Are you sure you want to ${toggleModal.config?.is_active ? 'disable' : 'enable'} "${toggleModal.config?.name}"?`}
        confirmText={toggleModal.config?.is_active ? "Disable" : "Enable"}
        cancelText="Cancel"
        type="warning"
      />

      {/* Filter Toggle Confirmation Modal */}
      <ConfirmationModal
        isOpen={filterModal.isOpen}
        onClose={() => setFilterModal({ isOpen: false, config: null })}
        onConfirm={confirmFilterToggle}
        title={filterModal.config?.filter_enabled ? "Disable Filter" : "Enable Filter"}
        message={`Are you sure you want to ${filterModal.config?.filter_enabled ? 'disable' : 'enable'} filter for "${filterModal.config?.name}"?`}
        confirmText={filterModal.config?.filter_enabled ? "Disable" : "Enable"}
        cancelText="Cancel"
        type="warning"
      />
    </MainLayout>
  );
};

export default EntryExitPage;